/**
 * Social sign-in — Apple JS SDK и Google Identity Services.
 *
 * Оба провайдера включаются только при заданных env-переменных:
 *   VITE_APPLE_CLIENT_ID + VITE_APPLE_REDIRECT_URI  → Apple
 *   VITE_GOOGLE_CLIENT_ID                            → Google
 *
 * Без них `isAppleEnabled()`/`isGoogleEnabled()` возвращают false, кнопки
 * в форме остаются задизейбленными, ни один внешний скрипт не грузится.
 *
 * Токены отдаются на backend в существующие мутации AppleAuth/GoogleAuth
 * (см. api/operations/Auth/AuthMutations.graphql) — те же, что использует iOS.
 */

const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;
const APPLE_REDIRECT_URI = import.meta.env.VITE_APPLE_REDIRECT_URI as
  string | undefined;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

const APPLE_SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const GOOGLE_SDK_URL = 'https://accounts.google.com/gsi/client';

export function isAppleEnabled(): boolean {
  return Boolean(APPLE_CLIENT_ID && APPLE_REDIRECT_URI);
}

export function isGoogleEnabled(): boolean {
  return Boolean(GOOGLE_CLIENT_ID);
}

/** Есть ли хоть один соцпровайдер — по этому решаем, показывать ли блок «или». */
export function hasSocialAuth(): boolean {
  return isAppleEnabled() || isGoogleEnabled();
}

export interface AppleCredential {
  /** identityToken (JWT) — уходит в мутацию appleAuth. */
  token: string;
  /** Apple отдаёт имя только при самом первом входе. */
  firstname?: string;
  lastname?: string;
}

// ── Загрузка сторонних SDK ────────────────────────────────────────────

const scriptPromises = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const existing = scriptPromises.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      scriptPromises.delete(src);
      reject(new Error(`Не удалось загрузить ${src}`));
    };
    document.head.appendChild(el);
  });

  scriptPromises.set(src, promise);
  return promise;
}

// ── Apple ─────────────────────────────────────────────────────────────

interface AppleAuthResponse {
  authorization?: { id_token?: string };
  user?: { name?: { firstName?: string; lastName?: string } };
}

interface AppleIdGlobal {
  auth: {
    init(config: {
      clientId: string;
      scope: string;
      redirectURI: string;
      usePopup: boolean;
    }): void;
    signIn(): Promise<AppleAuthResponse>;
  };
}

let appleInited = false;

/**
 * Открывает popup Apple Sign-In и возвращает identityToken.
 * Бросает ошибку, если провайдер не сконфигурирован или пользователь отменил.
 */
export async function signInWithApple(): Promise<AppleCredential> {
  if (!isAppleEnabled()) throw new Error('Apple Sign-In не сконфигурирован');

  await loadScript(APPLE_SDK_URL);
  const appleId = (window as unknown as { AppleID?: AppleIdGlobal }).AppleID;
  if (!appleId) throw new Error('Apple SDK недоступен');

  if (!appleInited) {
    appleId.auth.init({
      clientId: APPLE_CLIENT_ID!,
      scope: 'name email',
      redirectURI: APPLE_REDIRECT_URI!,
      usePopup: true,
    });
    appleInited = true;
  }

  const response = await appleId.auth.signIn();
  const token = response?.authorization?.id_token;
  if (!token) throw new Error('Apple не вернул identity token');

  return {
    token,
    firstname: response.user?.name?.firstName,
    lastname: response.user?.name?.lastName,
  };
}

// ── Google ────────────────────────────────────────────────────────────

interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

interface GoogleGlobal {
  accounts: {
    oauth2: {
      initTokenClient(config: {
        client_id: string;
        scope: string;
        callback: (response: GoogleTokenResponse) => void;
        error_callback?: (err: { type?: string }) => void;
      }): { requestAccessToken(): void };
    };
  };
}

/**
 * Открывает Google OAuth popup и возвращает access_token.
 *
 * Используем именно token flow (не One Tap с id_token): backend-мутация
 * googleAuth ждёт `googleAccessToken`, которым проверяет профиль через Google API.
 */
export async function signInWithGoogle(): Promise<string> {
  if (!isGoogleEnabled()) throw new Error('Google Sign-In не сконфигурирован');

  await loadScript(GOOGLE_SDK_URL);
  const google = (window as unknown as { google?: GoogleGlobal }).google;
  if (!google?.accounts?.oauth2) throw new Error('Google SDK недоступен');

  return new Promise<string>((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID!,
      scope: 'openid email profile',
      callback: (response) => {
        if (response.access_token) resolve(response.access_token);
        else
          reject(
            new Error(
              response.error_description ?? response.error ?? 'Google не вернул токен'
            )
          );
      },
      error_callback: (err) =>
        reject(
          new Error(
            err?.type === 'popup_closed'
              ? 'Окно входа Google закрыто'
              : 'Не удалось войти через Google'
          )
        ),
    });
    client.requestAccessToken();
  });
}
